import React, { useState, useRef, useEffect, useMemo } from 'react'
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { BiImageAdd } from "react-icons/bi";
import CountrySelect from '../CountrySelect';
import CheckboxSelect from '../CheckboxSelect';
import { BiFile } from "react-icons/bi";
import uuid from 'react-uuid';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ImageGalleryUploader from '../ImageUploadGallery';
import UrlListEditor from '../UrlListEditor';
import JoditEditor from 'jodit-react';

const theme = createTheme({
  typography: {
    fontFamily: ["Radio Canada Big", "Arial"].join(",")
  }
});

const AddProvider = (props) => {
    const [ providerId, setProviderId ] = useState(null);
    const [ providerName, setProviderName ] = useState("");
    const [ logoUrl, setLogoUrl ] = useState([]);
    const [ website, setWebsite ] = useState("");
    const [ providerSummary, setProviderSummary ] = useState("");
    const [ providerDescription, setProviderDescription ] = useState("");
    const [ providerLocation, setProviderLocation ] = useState(null);
    const [ techValue, setTechValue ] = useState([]);
    const [ safetValue, setSafetValue ] = useState([]);
    const [ providerContact, setProviderContact ] = useState("");
    const [ providerSocials, setProviderSocials ] = useState([]);
    const [ sdgValue, setSdgValue ] = useState([]);
    const [ imageUrls, setImageUrls ] = useState([]);
    const [ documents, setDocuments ] = useState([]);
    const [ errorText, setErrorText ] = useState(null);


    const editor = useRef(null);

    const editorConfig = useMemo(() => ({
            readonly: false,
            buttons: ['bold','italic','underline','ul','ol','undo','redo'],
            statusbar: false,
            defaultActionOnPaste: 'insert_as_text',
            askBeforePasteFromWord: false,
            pasteHTML: '',
            pastePlain: true,
            placeholder: 'Detailed profile description'
        }),
        []
    );

    const handleSubmit = async (event) => {
        event.preventDefault();

        setErrorText(null);

        if (website && !website.startsWith("https://")) {
            setErrorText("Website URL must start with https://");
            return;
        }

        const row = {
            name: providerName,
            logo: logoUrl.length > 0 ? logoUrl[0] : null,
            website: website,
            summary: providerSummary,
            location: providerLocation ? providerLocation.name : null,
            technology_types: techValue.map((i) => (i.label)),
            sectors: safetValue.map((i) => (i.label)),
            contact: providerContact,
            social_media: providerSocials.map((i) => (i.link)),
            sdgs: sdgValue.map((i) => (i.number)),
            image_urls: imageUrls,
            documents: documents,
            description: providerDescription,
        };

        //console.log(row);

        const { error } = await props.db.schema('api').from('tech_providers').insert(row);
        if (error) {
            console.log(error);
            setErrorText("Error saving provider");
            return;
        }

        resetFields();

        console.log(`Saved provider ${row.name}`);
    }

    const resetFields = () => {
        setProviderName("");
        setLogoUrl([]);
        setWebsite("");
        setProviderSummary("");
        setProviderDescription("");
        setProviderLocation(null);
        setTechValue([]);
        setSafetValue([]);
        setProviderContact("");
        setProviderSocials([]);
        setSdgValue([]);
        setImageUrls([]);
        setDocuments([]);
        setProviderId(uuid());
    }

    const doLogoUpload = async (imgFile, filetype) => {
        return await doUpload("logo", imgFile, filetype);
    }

    const doImagesUpload = async (imgFile, filetype) => {
        return await doUpload("main", imgFile, filetype);
    }

    const doUpload = async (prefix, imgFile, filetype) => {
        var theUrl = await props.db.storage
            .from('provider_images')
            .upload(`${prefix}_${providerId}_${imgFile.name}`, imgFile, {
              contentType: filetype,
        })
        .then((result) => {
            const {data} = props.db.storage.from('provider_images').getPublicUrl(result.data.path);
            return data.publicUrl;
        })
        .catch((error) => {
            console.log(error);
            throw error;
        });

        return theUrl;
    }

    useEffect(() => {
        if (!providerId) {
            setProviderId(uuid());
        }
    }, []);

    return (
<ThemeProvider theme={theme}>
<LocalizationProvider dateAdapter={AdapterDayjs}>
    <Card sx={{ maxWidth: "80vw", margin: "auto", marginTop: "50px" }}>
        <CardContent>
            <Typography variant="h4">
                Enter all of the details for your Tech Provider.
            </Typography>
            <Box component={"form"} id="theForm" onSubmit={handleSubmit}>
                <TextField id="providerName" value={providerName} onChange={(event) => setProviderName(event.target.value)} label="Organisation Name" variant="standard" sx={{width: "100%"}} />  
                <p/>
                <TextField id="providerSummary" value={providerSummary} onChange={(event) => setProviderSummary(event.target.value)} label="One sentence tagline" variant="standard" multiline rows={2} sx={{width: "100%"}} />  
                <p/>
                <JoditEditor
                    ref={editor}
                    value={providerDescription}
                    config={editorConfig}
                    onBlur={newContent => setProviderDescription(newContent)} 
                    onChange={newContent => {}}
                />
                {/* <TextField id="providerDescription" value={providerDescription} onChange={(event) => setProviderDescription(event.target.value)} label="Detailed profile description" variant="standard" multiline rows={4} sx={{width: "100%"}} />   */}
                <p/>
                <CountrySelect value={providerLocation} setValue={setProviderLocation} />
                <p/>
                <CheckboxSelect options={safetOptions} value={safetValue} setValue={setSafetValue} id={"safetTheme"} name={"Sectors supported"}/>
                <p/>
                <CheckboxSelect options={techOptions} value={techValue} setValue={setTechValue} id={"techTypes"} name={"Technologies offered"}/>
                <p/>
                <CheckboxSelect options={sdgOptions} value={sdgValue} setValue={setSdgValue} id={"sdgs"} name={"SDGs served"}/>
                <p/>
                <TextField id="providerWebsite" value={website} onChange={(event) => setWebsite(event.target.value)} label="Website" variant="standard" sx={{width: "100%"}} />  
                <p/>
                <TextField id="providerContact" value={providerContact} onChange={(event) => setProviderContact(event.target.value)} label="Contact email" variant="standard" sx={{width: "100%"}} />  
                <p/>
                <Typography component="p" variant="body" color="text.secondary" sx={{pt:2, pb:2}}>
                    Share your social media links:
                </Typography>
                <UrlListEditor links={providerSocials} setLinks={setProviderSocials} maxItems={5} />

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    Share any documents to feature on your profile:
                </Typography>
                <UrlListEditor links={documents} setLinks={setDocuments} maxItems={5} />

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    Upload your logo (square is best):
                </Typography>
                <ImageGalleryUploader images={logoUrl} setImages={setLogoUrl} maxImages={1} onUpload={doLogoUpload}/>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                    Upload your showcase images:
                </Typography>
                <ImageGalleryUploader images={imageUrls} setImages={setImageUrls} maxImages={10} onUpload={doImagesUpload}/>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:5, pb:2}}>
                By submitting this form, you understand and agree that the information and images you provide may be posted on SAFET's website. 
                You agree that your submission is subject to SAFET's  Privacy Policy and Terms of Use, and you understand that your information 
                and images will be collected, stored, and used as described in those policies. 
                </Typography>

                <Typography component="p" variant="body" color="text.secondary" sx={{pt:0, pb:2}}>
                You confirm that you have the right to share the information and images you submit.
                </Typography>

                { errorText ? <Typography color="error" sx={{mt:2}}>{errorText}</Typography> : <div/> }
            </Box>
        </CardContent>
        <CardActions>
            <Button variant="outlined" size="small" onClick={resetFields}>Reset</Button>
            <Button form="theForm" type="submit" variant="contained" size="small">Save</Button>
        </CardActions>
        <p/>
    </Card>
</LocalizationProvider>
</ThemeProvider>
    );
}

// TODO: Obtain these options from the database.

const safetOptions = [
    { label: "Fisheries Management" },
    { label: "Fisheries Enforcement" },
    { label: "Fishing Industry (Commercial)" },
    { label: "Small-scale Fishing" },
    { label: "Government Policy" },
    { label: "Water quality monitoring" },
    { label: "Disease and stress early detection" },
    { label: "Predictive analytics / risk forecasting" },
    { label: "Farm management & productivity" },
    { label: "Sustainability and resilience" },
    { label: "Seafood Buyer" },
    { label: "Scientific Research" },
    { label: "Conservation" },
    { label: "Advocacy" },
    { label: "Other" },
];

const techOptions = [
    { label: "Electronic Monitoring"},
    { label: "Vessel Tracking"},
    { label: "Drones and Uncrewed Vehicles"},
    { label: "Coastal Radar"},
    { label: "Electronic Logbooks"},
    { label: "Traceability Systems"},
    { label: "Surveillance Systems"},
    { label: "Mapping (GIS) Systems"},
    { label: "Artificial Intelligence"},
    { label: "Machine Learning"},
    { label: "Environmental DNA"},
    { label: "In-situ Sensors"},
    { label: "By-catch Reduction Devices"},
    { label: "Aquaculture technology"},
    { label: "Early warning & monitoring systems"},
    { label: "Decision-support tools"},
    { label: "Electric Vessels"},
    { label: "Other"},
];

const stageOptions = [
    { label: "Idea"},
    { label: "Proposal"},
    { label: "Planned"},
    { label: "Funded"},
    { label: "In Progress"},
    { label: "Complete"},
];

const impactOptions = [
    { label: "Unknown"},
    { label: "Estimated"},
    { label: "Partially Measured"},
    { label: "Fully Measured"},
];

const sdgOptions = [
    { label: "1 No Poverty", number: 1},
    { label: "2 Zero Hunger", number: 2},
    { label: "3 Good Health and Wellbeing", number: 3},
    { label: "4 Quality Education", number: 4},
    { label: "5 Gender Equality", number: 5},
    { label: "6 Clean Water and Sanitation", number: 6},
    { label: "7 Affordable and Clean Energy", number: 7},
    { label: "8 Decent Work", number: 8},
    { label: "9 Industry, Innovation, Infrastructure", number: 9},
    { label: "10 Reduced Inequalities", number: 10},
    { label: "11 Sustainable Cities", number: 11},
    { label: "12 Responsible Consumption", number: 12},
    { label: "13 Climate Action", number: 13},
    { label: "14 Life Below Water", number: 14},
    { label: "15 Life On Land", number: 15},
    { label: "16 Peace, Justice, Strong Institutions", number: 16},
    { label: "17 Partnerships for the Goals", number: 17},
];

const outcomeOptions = [
    { label: "MPA Designation"},
    { label: "Sustainable Fishing"},
    { label: "Certification"},
    { label: "Fisheries Improvement"},
    { label: "Food Security"},
    { label: "Reduced Poverty"},
    { label: "Greater Equality"},
    { label: "Improved Livelihoods"},
    { label: "Safeguard Human Rights"},
    { label: "Climate Mitigation"},
    { label: "Climate Adaptation"},
];

const speciesOptions = [
    { label: "Tuna"},
    { label: "Swordfish"},
    { label: "Squid"},
    { label: "Sharks"},
    { label: "Whales"},
    { label: "Dolphins"},
    { label: "Rays"},
    { label: "Freshwater Finfish"},
    { label: "Tilapia"},
    { label: "Catfish"},
];

export default AddProvider;